import { useCallback } from "react";
import { POCItemsDocumentAnswer } from "../../messages/PatientDocuments";
import { TaskWhenToPerform } from "../../messages/PlanOfCare";

interface UsePOCDocumentQuestionProps {
  questionId: number;
  currentAnswer: POCItemsDocumentAnswer[] | null;
  readonly: boolean;
  onClickWhileDisabled: () => void;
  onInsertAnswer: (id: number, answer: POCItemsDocumentAnswer[]) => void;
}

export function usePOCDocumentQuestion({
  questionId,
  currentAnswer,
  readonly,
  onClickWhileDisabled,
  onInsertAnswer,
}: UsePOCDocumentQuestionProps) {
  const handleTaskCheck = useCallback(
    (itemIndex: number) => {
      if (readonly) {
        onClickWhileDisabled();
        return;
      }

      const currentAnswers = currentAnswer ?? [];
      const existingAnswerIndex = currentAnswers.findIndex((answer) => answer.idx === itemIndex);

      if (existingAnswerIndex >= 0) {
        // Remove the answer if it exists
        const newAnswers = currentAnswers.filter((_, index) => index !== existingAnswerIndex);
        onInsertAnswer(questionId, newAnswers);
      } else {
        // Add new answer
        const newAnswer: POCItemsDocumentAnswer = {
          idx: itemIndex,
          attributes: { type: "AsRequestedByPatient" },
        };
        onInsertAnswer(questionId, [...currentAnswers, newAnswer]);
      }
    },
    [currentAnswer, onClickWhileDisabled, onInsertAnswer, questionId, readonly]
  );

  const handleWhenToPerformChange = useCallback(
    (itemIndex: number, whenToPerform: TaskWhenToPerform) => {
      if (readonly) return;

      const currentAnswers = currentAnswer ?? [];
      const existingAnswerIndex = currentAnswers.findIndex((answer) => answer.idx === itemIndex);

      if (existingAnswerIndex >= 0) {
        const newAnswers = [...currentAnswers];
        newAnswers[existingAnswerIndex] = {
          ...newAnswers[existingAnswerIndex],
          attributes: whenToPerform,
        };
        onInsertAnswer(questionId, newAnswers);
      }
    },
    [currentAnswer, onInsertAnswer, questionId, readonly]
  );

  const handleFrequencyChange = useCallback(
    (itemIndex: number, frequency: number | null, visitsInAWeek: number | null) => {
      if (readonly) return;

      const currentAnswers = currentAnswer ?? [];
      const existingAnswerIndex = currentAnswers.findIndex((answer) => answer.idx === itemIndex);

      if (existingAnswerIndex >= 0) {
        const currentAnswer = currentAnswers[existingAnswerIndex];
        if (currentAnswer.attributes.type === "OnDaysOfWeek") {
          const newAnswers = [...currentAnswers];
          newAnswers[existingAnswerIndex] = {
            ...currentAnswer,
            attributes: {
              ...currentAnswer.attributes,
              frequency: { frequency, visitsInAWeek },
            },
          };
          onInsertAnswer(questionId, newAnswers);
        }
      }
    },
    [currentAnswer, onInsertAnswer, questionId, readonly]
  );

  const handleNotesChange = useCallback(
    (itemIndex: number, notes: string) => {
      if (readonly) return;

      const currentAnswers = currentAnswer ?? [];
      const existingAnswerIndex = currentAnswers.findIndex((answer) => answer.idx === itemIndex);

      if (existingAnswerIndex >= 0) {
        const newAnswers = [...currentAnswers];
        newAnswers[existingAnswerIndex] = {
          ...newAnswers[existingAnswerIndex],
          notes,
        };
        onInsertAnswer(questionId, newAnswers);
      }
    },
    [currentAnswer, onInsertAnswer, questionId, readonly]
  );

  return {
    handleTaskCheck,
    handleWhenToPerformChange,
    handleFrequencyChange,
    handleNotesChange,
  };
}
